import { Check, Sparkles } from "lucide-react";
import Seo, { breadcrumb } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { CoveButton } from "@/components/CoveButton";
import { Reveal, Pop, FloatingBlob } from "@/components/motion";
import {
  IMAGES,
  SITE,
  CST,
  BENEFITS,
  CONDITIONS,
  CONDITIONS_INTRO,
  IN_SESSION,
  IN_SESSION_CLOSING,
  TESTIMONIALS,
} from "@/data/content";

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Craniosacral Therapy",
  provider: { "@type": "Organization", name: "Cranio Cove" },
  areaServed: SITE.serviceArea,
  description:
    "Gentle, hands-on craniosacral therapy sessions supporting relaxation and nervous-system regulation.",
};

export default function Therapy() {
  return (
    <>
      <Seo
        title="Craniosacral Therapy"
        description="An approachable guide to craniosacral therapy at Cranio Cove, what it is, what happens in a session, its gentle nature, and important, responsible limitations."
        path="/craniosacral-therapy"
        image={IMAGES.sandWater}
        schema={serviceSchema}
      />
      <PageHero
        overline="The therapy"
        titleLines={[]}
        breadcrumb={breadcrumb([{ name: "Home", path: "/" }, { name: "Craniosacral Therapy", path: "/craniosacral-therapy" }])}
      />

      {/* Merged hero, photo on top, single heading + description below */}
      <section className="px-6 md:px-12 pt-2 pb-16 md:pb-24" data-testid="therapy-what">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <div
              className="relative mx-auto overflow-hidden shadow-[0_30px_60px_rgba(41,54,80,0.14)] max-w-[520px]"
              style={{ borderTopLeftRadius: "50% 40%", borderTopRightRadius: "50% 40%" }}
            >
              <img
                src={IMAGES.lauraStudio}
                alt="Laura McLelland, Craniosacral Practitioner"
                loading="lazy"
                className="w-full h-[460px] md:h-[600px] object-cover object-[center_65%]"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-12 text-xs tracking-[0.28em] uppercase text-cove-duskyrose">
              What is CST?
            </p>
            <h1 className="mt-4 font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.98] text-cove-slate">
              Gentle, Effective, <span className="italic text-cove-sage">Profound</span>
            </h1>
            <div className="mt-8 space-y-5 text-lg leading-relaxed text-cove-olive max-w-2xl mx-auto text-left md:text-center">
              {CST.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section
        className="relative px-6 md:px-12 py-16 md:py-24 bg-gradient-to-br from-cove-blossom/40 via-cove-restorative to-cove-mutedblue/25 overflow-hidden"
        data-testid="therapy-benefits"
      >
        <FloatingBlob color="sage" size={320} className="top-0 -left-24" delay={0.2} />
        <div className="relative mx-auto max-w-[1400px]">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4">Benefits of CST</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] text-cove-slate max-w-3xl">
              What clients often <span className="italic text-cove-rose">notice</span>
            </h2>
          </Reveal>
          <ul className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => {
              const tints = [
                "bg-cove-blossom/60",
                "bg-cove-mutedblue/20",
                "bg-cove-restorative",
                "bg-cove-sage/30",
                "bg-cove-blush/50",
                "bg-cove-bluestone/20",
              ];
              return (
                <li key={i}>
                  <Reveal delay={i * 0.06}>
                    <div className={`h-full rounded-[1.5rem] ${tints[i % tints.length]} border border-cove-slate/10 p-6 md:p-7 flex items-start gap-4`}>
                      <Check size={18} className="mt-1 text-cove-groundedolive shrink-0" aria-hidden="true" />
                      <p className="text-cove-olive leading-relaxed">{b}</p>
                    </div>
                  </Reveal>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Conditions CST can help with */}
      <section className="px-6 md:px-12 py-16 md:py-24" data-testid="therapy-conditions">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4">
              How CST can help you
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] text-cove-slate max-w-3xl">
              Benefits of <span className="italic text-cove-rose">Craniosacral Therapy</span>
            </h2>
            <p className="mt-6 max-w-3xl text-lg text-cove-olive leading-relaxed">
              {CONDITIONS_INTRO}
            </p>
          </Reveal>
          <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-5">
            {CONDITIONS.map((c, i) => (
              <li key={i}>
                <Reveal delay={i * 0.04}>
                  <div className="flex items-baseline gap-3 border-b border-cove-slate/10 pb-4">
                    <span className="h-1.5 w-1.5 rounded-full bg-cove-duskyrose shrink-0 translate-y-[-2px]" aria-hidden="true" />
                    <p className="font-serif italic text-lg md:text-xl text-cove-slate leading-snug">
                      {c}
                    </p>
                  </div>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What to Expect in a Session (reworded) */}
      <section
        className="relative px-6 md:px-12 py-20 md:py-28 bg-gradient-to-tr from-cove-sage/25 via-cove-restorative to-cove-blossom/50 overflow-hidden"
        data-testid="therapy-during"
      >
        <FloatingBlob color="blush" size={340} className="top-10 -right-20" delay={0.2} />
        <div className="relative mx-auto max-w-[1400px]">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4">In a session</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] text-cove-slate max-w-2xl">
              Soft, and <span className="italic text-cove-rose">led by you</span>
            </h2>
            <p className="mt-6 max-w-2xl text-lg text-cove-olive leading-relaxed">
              What to expect in a session:
            </p>
          </Reveal>
          <ul className="mt-10 grid md:grid-cols-2 gap-5">
            {IN_SESSION.map((line, i) => {
              const tints = ["bg-cove-blossom/60", "bg-cove-mutedblue/20", "bg-cove-restorative", "bg-cove-blush/50"];
              return (
                <li key={i}>
                  <Reveal delay={i * 0.08}>
                    <Pop className={`h-full rounded-[1.5rem] ${tints[i % tints.length]} border border-cove-slate/10 p-6 md:p-7 flex items-start gap-4`}>
                      <Sparkles size={22} className="text-cove-gold shrink-0 mt-1" aria-hidden="true" />
                      <p className="text-cove-olive leading-relaxed">{line}</p>
                    </Pop>
                  </Reveal>
                </li>
              );
            })}
          </ul>
          <Reveal delay={0.4}>
            <p className="mt-10 max-w-2xl font-serif italic text-xl md:text-2xl leading-snug text-cove-slate">
              {IN_SESSION_CLOSING}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Series-of-sessions note (before disclaimer) */}
      <section className="px-6 md:px-12 py-14 md:py-20" data-testid="therapy-series">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4">A note on your journey</p>
            <p className="font-serif text-2xl md:text-[1.7rem] leading-snug text-cove-slate">
              Most people benefit from a series of CST sessions, especially when healing long-term
              or complex conditions.
            </p>
            <p className="mt-5 text-cove-olive leading-relaxed">
              Each visit builds on the last. Your feedback helps guide future sessions and ensures
              we're supporting your unique healing process.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Testimonials — quiet reflections */}
      <section
        className="relative px-6 md:px-12 py-20 md:py-28 bg-gradient-to-b from-cove-blossom/40 via-cove-restorative to-cove-sage/25 overflow-hidden"
        data-testid="therapy-testimonials"
      >
        <FloatingBlob color="sage" size={340} className="top-10 -left-20" delay={0.2} />
        <div className="relative mx-auto max-w-[1400px]">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4 text-center">
              In their words
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] text-cove-slate text-center">
              Quiet <span className="italic text-cove-duskyrose">reflections</span>
            </h2>
          </Reveal>
          <ul className="mt-14 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => {
              const tints = ["bg-cove-cream", "bg-cove-blossom/60", "bg-cove-mutedblue/25"];
              return (
                <li key={i}>
                  <Reveal delay={i * 0.1}>
                    <div className={`h-full rounded-[1.75rem] ${tints[i % 3]} border border-cove-slate/10 p-7 md:p-8`}>
                      <span className="font-serif text-4xl text-cove-rose/70 leading-none">&ldquo;</span>
                      <p className="mt-3 font-serif text-lg italic leading-relaxed text-cove-slate">
                        {t.quote}
                      </p>
                      <p className="mt-6 text-sm text-cove-slate">{t.name}</p>
                      <p className="text-xs text-cove-taupe">{t.detail}</p>
                    </div>
                  </Reveal>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Limits, a responsible reminder */}
      <section className="px-6 md:px-12 py-20 md:py-28" data-testid="therapy-limits">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="rounded-[2rem] md:rounded-[3rem] border border-cove-slate/15 bg-cove-cream/60 px-8 md:px-16 py-14 md:py-16">
              <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-6">
                Important to know
              </p>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1] text-cove-slate max-w-3xl">
                Limitations & a responsible reminder
              </h2>
              <div className="mt-8 space-y-5 text-lg leading-relaxed text-cove-olive max-w-3xl">
                <p>
                  Craniosacral therapy is a complementary practice intended to support relaxation
                  and general wellbeing. It does not diagnose, treat, or cure any medical
                  condition, and it makes no guarantees of specific results.
                </p>
                <p>
                  It is not a substitute for appropriate medical care. If you have a health
                  concern, please continue to consult your doctor or qualified healthcare provider.
                  Sessions are offered alongside, never in place of, the care of your medical
                  team.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-28 md:pb-40" data-testid="therapy-cta">
        <div className="mx-auto max-w-[1400px] text-center">
          <Reveal>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] text-cove-slate">
              Curious to try?
            </h2>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <CoveButton calendly variant="primary" testId="therapy-book-button">
                Book a Session
              </CoveButton>
              <CoveButton to="/what-to-expect" variant="secondary" testId="therapy-expect-button">
                What to Expect
              </CoveButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
